/**
 * Authentication state.
 *
 * Tokens live in `localStorage` via zustand's persist middleware so a refresh
 * does not sign the user out. The store is deliberately dumb -- it holds tokens
 * and the user record; the refresh dance lives in `lib/api.ts`, which is the one
 * place allowed to talk to the network.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { CurrentUser } from '@/types/api'

interface AuthState {
  access: string | null
  refresh: string | null
  user: CurrentUser | null
  setSession: (session: { access: string; refresh: string; user: CurrentUser }) => void
  setAccess: (access: string) => void
  setUser: (user: CurrentUser) => void
  clear: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      access: null,
      refresh: null,
      user: null,
      setSession: ({ access, refresh, user }) => set({ access, refresh, user }),
      setAccess: (access) => set({ access }),
      setUser: (user) => set({ user }),
      clear: () => set({ access: null, refresh: null, user: null }),
    }),
    {
      name: 'knysa-library-auth',
      partialize: (state) => ({
        access: state.access,
        refresh: state.refresh,
        user: state.user,
      }),
    },
  ),
)

/** Non-reactive accessors, for use inside axios interceptors. */
export const authSnapshot = () => useAuthStore.getState()

export const isStaff = (user: CurrentUser | null): boolean => Boolean(user?.is_staff)
